import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Fragment } from 'react';

import { useAuth } from '@/entities/user';
import { ResetProgressMenuItem } from '@/features/reset-course-progress';
import { SignOut } from '@/features/sign-out';
import { SystemAlertButton } from '@/features/system-alerts';
import { Button, ChatBubble, UserIcon } from '@/shared';
import { UserRole } from '@/shared/api';
import { useLoginUrlWithRedirect } from '@/shared/hooks';
import { cn } from '@/shared/utils';

import { getSupportNavigation, userNavigation } from '..';
import { HeaderMenuItem } from './header-menu-item';
import { WorkspaceSupport } from './workspace-support';

export function HeaderNav({
	variant = 'default',
	onAction,
}: {
	variant?: 'default' | 'workspace';
	onAction?: (action: string) => void;
}) {
	const { user, loading } = useAuth();
	const loginUrl = useLoginUrlWithRedirect();
	const workspace = variant === 'workspace';

	if (loading) {
		return null;
	}

	if (!user) {
		return (
			<>
				<Button
					href={loginUrl}
					variant="outline"
					size="40"
					onClick={() => onAction?.('login')}
				>
					<span className="uppercase">Войти</span>
				</Button>
				{/* TODO: вернуть после фикса бэка на анонимов */}
				{/* 
				<Button
					className="hidden md:flex"
					href="/courses/algo-big-tech/payment"
					size="40"
				>
					<span className="uppercase">Купить полный курс</span>
				</Button>
				*/}
			</>
		);
	}

	return (
		<>
			<div id="portal-navigation-block" />
			<SystemAlertButton onOpen={() => onAction?.('notifications_open')} />
			{workspace ? (
				<WorkspaceSupport
					personalManager={user.personalManager}
					onAction={onAction}
				/>
			) : (
				<Menu as="div" className="relative">
					<MenuButton as={Fragment}>
						{() => (
							<Button size="40" className="w-10 !px-2" variant="gray">
								<ChatBubble className="size-5" />
							</Button>
						)}
					</MenuButton>
					<MenuItems
						transition
						className="absolute right-5 z-10 -mt-1 w-64 origin-top-right rounded-lg bg-white p-2 shadow-listbox ring-1 ring-inset ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
					>
						{getSupportNavigation(user.personalManager).map((item, key) => (
							<HeaderMenuItem key={key} {...item} />
						))}
					</MenuItems>
				</Menu>
			)}
			<Menu as="div" className="relative">
				<MenuButton as={Fragment}>
					{() =>
						workspace ? (
							<button
								type="button"
								aria-label="Открыть меню пользователя"
								className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-5 text-sm font-medium text-gray-3 transition-colors hover:bg-gray-4/60 motion-reduce:transition-none"
								onClick={(event) =>
									onAction?.(
										event.currentTarget.getAttribute('aria-expanded') === 'true'
											? 'account_close'
											: 'account_open',
									)
								}
							>
								{Array.from(user.login.trim())[0]?.toLocaleUpperCase(
									'ru-RU',
								) || <UserIcon aria-hidden="true" className="size-5" />}
							</button>
						) : (
							<Button
								size="40"
								className="w-10 !px-2 text-gray-3"
								variant="gray"
							>
								{user.role === 'admin' ? (
									<ShieldCheckIcon className="size-5" />
								) : (
									<UserIcon className="size-5" />
								)}
							</Button>
						)
					}
				</MenuButton>
				<MenuItems
					transition
					onKeyDown={(event) => {
						if (event.key === 'Escape') onAction?.('account_close');
					}}
					className={cn(
						'absolute right-5 z-10 -mt-1 w-max min-w-36 max-w-64 origin-top-right rounded-lg bg-white p-2 shadow-listbox ring-1 ring-inset ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
						workspace &&
							'right-0 mt-3 max-h-[calc(100svh-80px)] max-w-[min(256px,calc(100vw-32px))] overflow-y-auto motion-reduce:transition-none',
					)}
				>
					<MenuItem>
						<div className="mb-1 flex flex-col overflow-hidden text-ellipsis border-b px-3 py-2 text-sm">
							<span className="text-[10px] text-gray-3">логин:</span>
							<span className="text-[14px] leading-[12px]">{user.login}</span>
						</div>
					</MenuItem>
					<ResetProgressMenuItem />
					{[UserRole.Admin, UserRole.Editor].includes(user.role) && (
						<HeaderMenuItem
							name="Админка"
							href="/admin"
							onNavigate={() => onAction?.('admin')}
						/>
					)}
					{[UserRole.CommunityManager, UserRole.Admin].includes(user.role) && (
						<HeaderMenuItem
							name="Мониторинг клиентов"
							href="/client-monitoring"
							onNavigate={() => onAction?.('client_monitoring')}
						/>
					)}
					{[UserRole.CommunityManager, UserRole.Admin].includes(user.role) && (
						<HeaderMenuItem
							name="CRM"
							href="/crm"
							onNavigate={() => onAction?.('crm')}
						/>
					)}
					{userNavigation.map((item, key) => (
						<HeaderMenuItem
							key={key}
							{...item}
							onNavigate={() => onAction?.('finances')}
						/>
					))}

					<MenuItem>
						{({ focus }) => (
							<SignOut
								submit={
									<button
										type="submit"
										onClick={() => onAction?.('sign_out')}
										className={clsx(
											{
												'bg-gray-100 outline-none': focus,
											},
											'block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-white hover:outline-none',
										)}
									>
										Выйти
									</button>
								}
							/>
						)}
					</MenuItem>
				</MenuItems>
			</Menu>
			{/* TODO: вернуть после фикса бэка на анонимов */}
			{/* 
			{!user?.productsID?.length && (
				<Button
					className="hidden md:flex"
					href="/courses/algo-big-tech/payment"
					size="40"
				>
					<span className="uppercase">Купить полный курс</span>
				</Button>
			)} */}
		</>
	);
}
